import {
  PageBlueprint,
  createFrontendPlugin,
  createFrontendModule,
  createRouteRef,
} from '@backstage/frontend-plugin-api';
import { PluginHeaderActionBlueprint } from '@backstage/frontend-plugin-api';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

/**
 * Cheat-sheet companion for the live RBAC editor at `/rbac`.
 *
 * NOTE on naming: this page used to be called "Permissions Guide" and
 * lived under the "Help" sidebar group. It was renamed to "Permissions
 * reference" and moved next to the RBAC nav item so the relationship is
 * obvious — the RBAC plugin is the live EDITOR, this page is the static
 * REFERENCE that documents what the bootstrap defaults are. Two surfaces,
 * one mental model.
 *
 * Why we did NOT merge it into /rbac as tabs:
 *   The `@backstage-community/plugin-rbac` plugin's `page:rbac` extension
 *   provides its own `loader`, and the framework's `PageBlueprint`
 *   factory uses EITHER a loader OR sub-pages — not both (see
 *   node_modules/@backstage/frontend-plugin-api/dist/blueprints/PageBlueprint.esm.js).
 *   Overriding `page:rbac` to replace the loader would require us to
 *   re-implement the role detail / create-role / edit-role routes — but
 *   those internal page components (`RoleOverviewPage`, `EditRolePage`,
 *   `CreateRolePage`) are NOT exported by the plugin's `package.json#exports`.
 *   Forcing the merge would break core RBAC functionality. So we keep
 *   them as sibling sidebar entries, visually grouped.
 *
 * The extension id stays as `page:permissions-guide` (no `name`) so we
 * don't have to re-wire callers; only the human-facing title changes.
 */
const permissionsGuideRouteRef = createRouteRef();

const permissionsGuidePage = PageBlueprint.make({
  params: {
    path: '/permissions-guide',
    title: 'Permissions reference',
    icon: <MenuBookIcon />,
    routeRef: permissionsGuideRouteRef,
    // `noHeader: true` because PermissionsGuidePage already renders its
    // own `<Header>` (the blue banner). Without this, our custom
    // `BoschPageLayout` would stack a second blue banner on top of it.
    // See `packages/app/src/modules/theme/pageLayout.tsx`.
    noHeader: true,
    loader: () =>
      import('../../components/permissions/PermissionsGuidePage').then(m => (
        <m.PermissionsGuidePage />
      )),
  },
});

export const permissionsModule = createFrontendPlugin({
  pluginId: 'permissions-guide',
  routes: {
    permissionsGuide: permissionsGuideRouteRef,
  },
  extensions: [permissionsGuidePage],
});

/**
 * "Bridge" header action: a small `Permissions reference →` button that
 * appears in the upper-right of the /rbac banner and deep-links to
 * /permissions-guide. It's our pragmatic answer to the user's "can we
 * have these as tabs inside /rbac?" request — true tabs aren't possible
 * (see the JSDoc on `permissionsModule` above), so the next-best UX is a
 * one-click jump from the live editor to the cheat-sheet that explains
 * what every checkbox in there means.
 *
 * How the wiring works:
 *
 *   1. `PluginHeaderActionBlueprint` attaches every action to a single
 *      `actions` input on the global `api:app/plugin-header-actions`
 *      extension.
 *   2. `DefaultPluginHeaderActionsApi.fromActions(...)` keys each action
 *      by `node.spec.plugin.pluginId` — i.e. by the plugin that
 *      DECLARED the action, not by where the action is shown.
 *   3. So we wrap this extension in `createFrontendModule({ pluginId:
 *      'rbac', ... })` to "donate" it to the upstream rbac plugin's
 *      headerActions list.
 *   4. When the upstream `page:rbac`'s factory calls
 *      `headerActionsApi.getPluginHeaderActions('rbac')`, it gets back
 *      both upstream actions (none today) AND ours, and renders them in
 *      the `headerActions` slot of the BoschPageLayout banner.
 *
 * Adding more bridge actions later: declare another
 * `PluginHeaderActionBlueprint` in this same module, give it a unique
 * `name`, and it'll appear next to this one in the same banner.
 */
const permissionsReferenceBridgeAction = PluginHeaderActionBlueprint.make({
  name: 'permissions-reference-bridge',
  params: defineParams =>
    defineParams({
      // Loader returns the React element directly (no JSX needed at
      // module-eval time — the framework instantiates it lazily inside
      // ExtensionBoundary). Plain `<Link>` from react-router-dom keeps
      // the navigation client-side so the auth/identity state survives.
      loader: async () => (
        <Button
          component={Link}
          to="/admin?tab=permissions"
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<OpenInNewIcon />}
          // Inline style overrides the global `outlinedPrimary` rule
          // we ship in `theme/index.tsx` (which paints outlined-primary
          // as solid blue to match the scaffolder Register button).
          // Inside the blue banner we want this to read as a SECONDARY
          // action — translucent fill + white border + white text — so
          // the eye still lands on the role-list table first, not on
          // this bridge link.
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            color: '#FFFFFF',
          }}
        >
          Governance hub
        </Button>
      ),
    }),
});

export const rbacHeaderBridgeModule = createFrontendModule({
  pluginId: 'rbac',
  extensions: [permissionsReferenceBridgeAction],
});
